import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, Shield, Users, CheckSquare, Activity, CheckCircle, XCircle } from 'lucide-react';
import Tabs, { type Tab } from '../../components/common/Tabs';
import RoleModal from '../../components/admin/RoleModal';
import DataTable from '../../components/common/DataTable';
import type { Column } from '../../components/common/DataTable';
import { roleService, activityService } from '../../services';
import type { ActivityRecord } from '../../services/activity.service';
import { getRolePermissions, formatPermissions, type Permission } from '../../utils/permissions';
import { useToast } from '../../components/common/ToastContainer';
import { usePermissions } from '../../hooks/usePermissions';

const RoleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { canUpdate } = usePermissions();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [role, setRole] = useState<any | null>(null); // Using any to include role_type and permissions
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    const fetchRole = async () => {
      try {
        setLoading(true);
        const roleData = await roleService.getById(id);
        setRole(roleData);
      } catch (err: any) {
        setError(err.message || 'Failed to load role details');
      } finally {
        setLoading(false);
      }
    };

    fetchRole();
  }, [id]);

  const handleSaveRole = async (updatedRole: any) => {
    if (!id) return;
    try {
      const payload: any = {
        roleName: updatedRole.roleName,
        role_type: updatedRole.roleType,
        description: updatedRole.description,
        default_role: updatedRole.defaultRole ? 1 : 0,
      };

      // Auto-assign permissions based on role_type (unless Custom)
      if (updatedRole.roleType === 'Custom') {
        payload.permissions = updatedRole.permissions;
      }

      await roleService.update(id, payload);
      const roleData = await roleService.getById(id);
      setRole(roleData);
      setIsEditModalOpen(false);
    } catch (err: any) {
      toast.error('Update Failed', `Failed to update role: ${err.message}`);
    }
  };

  const roleForModal = role ? {
    id: role.id,
    roleName: role.roleName,
    roleType: (role as any).role_type || 'Custom',
    description: role.remark || '',
    defaultRole: (role as any).default_role === 1,
    permissions: (role as any).permissions || [],
  } : undefined;

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading role details...</div>;
  }

  if (error || !role) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">{error || 'Role not found'}</div>
      </div>
    );
  }

  const isSystemRole = role.status === 1;
  const roleType = (role as any).role_type || 'Custom';
  const rolePermissions = (role as any).permissions || getRolePermissions(roleType as any);

  const typeColors: Record<string, string> = {
    Admin: 'bg-red-100 text-red-700',
    Editor: 'bg-blue-100 text-blue-700',
    Viewer: 'bg-green-100 text-green-700',
    Custom: 'bg-purple-100 text-purple-700',
  };
  const colorClass = typeColors[roleType] || 'bg-gray-100 text-gray-700';

  // Tab 1: Overview
  const OverviewTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Role Information</h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-500">Role Name</label>
              <p className="text-gray-900">{role.roleName}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Role Type</label>
              <div className="mt-1">
                <span className={`px-3 py-1.5 text-sm font-medium rounded-full ${colorClass}`}>{roleType}</span>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Description</label>
              <p className="text-gray-900">{role.remark || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">System Role</label>
              <p className="text-gray-900">{isSystemRole ? 'Yes (cannot be edited/deleted)' : 'No'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Timestamps</h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-500">Created</label>
              <p className="text-gray-900">{role.createTime || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Last Updated</label>
              <p className="text-gray-900">{role.updateTime || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Created By</label>
              <p className="text-gray-900">{role.createBy || 'N/A'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Tab 2: Users (real data from API)
  const UsersTab = () => {
    const [roleUsers, setRoleUsers] = useState<any[]>([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [usersTotal, setUsersTotal] = useState(0);

    useEffect(() => {
      if (!id) return;
      const fetchUsers = async () => {
        try {
          setUsersLoading(true);
          const result = await roleService.getUsersByRole(id, { pageSize: 50 });
          setRoleUsers(result.items || []);
          setUsersTotal(result.pagination?.total || 0);
        } catch {
          setRoleUsers([]);
        } finally {
          setUsersLoading(false);
        }
      };
      fetchUsers();
    }, [id]);

    const userColumns: Column<any>[] = [
      {
        key: 'userRealName', label: 'Name', sortable: true,
        render: (u) => (
          <button
            onClick={() => navigate(`/admin/users/${u.id}`)}
            className="text-primary-600 hover:text-primary-800 hover:underline font-medium"
          >
            {u.userRealName || u.username}
          </button>
        ),
      },
      { key: 'email', label: 'Email', sortable: true, render: (u) => <span className="text-sm">{u.email || '-'}</span> },
      {
        key: 'status', label: 'Status', sortable: true,
        render: (u) => (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${u.status === 0 ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-700'}`}>
            {u.status === 0 ? 'Active' : 'Disabled'}
          </span>
        ),
      },
      {
        key: 'assignedAt', label: 'Assigned Date', sortable: true,
        render: (u) => <span className="text-sm">{u.assignedAt ? new Date(u.assignedAt).toLocaleDateString() : '-'}</span>,
      },
    ];

    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-500">{usersTotal} users with this role</p>
        <DataTable
          columns={userColumns}
          data={roleUsers}
          searchPlaceholder="Search users..."
          loading={usersLoading}
        />
      </div>
    );
  };

  // Tab 3: Permissions
  const PermissionsTab = () => (
    <div className="space-y-6">
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Role Type & Permissions</h3>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-gray-500">Role Type</label>
            <div className="mt-2">
              <span className={`px-3 py-1.5 text-sm font-medium rounded-full ${colorClass}`}>{roleType}</span>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Effective Permissions</label>
            <p className="text-gray-900 mt-1">{formatPermissions(rolePermissions)}</p>
          </div>
        </div>
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Permission Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(['create', 'read', 'update', 'delete'] as Permission[]).map((permission) => {
            const hasPermission = rolePermissions.includes(permission);
            return (
              <div key={permission} className={`p-4 border rounded-lg ${hasPermission ? 'border-success-300 bg-success-50' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {hasPermission ? (
                      <CheckCircle className="w-5 h-5 text-success-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-gray-400" />
                    )}
                    <span className={`font-medium ${hasPermission ? 'text-success-900' : 'text-gray-500'}`}>
                      {permission.charAt(0).toUpperCase() + permission.slice(1)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card bg-blue-50 border border-blue-200">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">RBAC Information</h3>
        <p className="text-sm text-blue-800">
          <strong>Scope:</strong> Roles only apply to Tenant Admins (type=2). SuperAdmins (type=1) bypass all role checks and have full system access. MobileClients (type=4) use a different permission model.
        </p>
      </div>
    </div>
  );

  // Tab 4: Activity (real data from API)
  const ActivityTab = () => {
    const [activities, setActivities] = useState<ActivityRecord[]>([]);
    const [activitiesLoading, setActivitiesLoading] = useState(true);
    const [activityTotal, setActivityTotal] = useState(0);

    useEffect(() => {
      if (!id) return;
      const fetchActivity = async () => {
        try {
          setActivitiesLoading(true);
          const result = await activityService.list({ resourceType: 'role', resourceId: id, pageSize: 20 });
          setActivities(result.items || []);
          setActivityTotal(result.pagination?.total || 0);
        } catch {
          setActivities([]);
        } finally {
          setActivitiesLoading(false);
        }
      };
      fetchActivity();
    }, [id]);

    const actionColors: Record<string, string> = {
      create: 'bg-green-100 text-green-700',
      update: 'bg-blue-100 text-blue-700',
      delete: 'bg-red-100 text-red-700',
      assign: 'bg-teal-100 text-teal-700',
      unassign: 'bg-amber-100 text-amber-700',
    };

    const activityColumns: Column<ActivityRecord>[] = [
      {
        key: 'created_at', label: 'Timestamp', sortable: true,
        render: (a) => <span className="text-sm">{a.created_at ? new Date(a.created_at).toLocaleString() : '-'}</span>,
      },
      {
        key: 'action', label: 'Action', sortable: true,
        render: (a) => <span className={`px-2 py-1 text-xs font-medium rounded-full ${actionColors[a.action] || 'bg-gray-100 text-gray-700'}`}>{a.action}</span>,
      },
      { key: 'user_email', label: 'User', sortable: true, render: (a) => <span className="text-sm">{a.user_email || '-'}</span> },
      { key: 'resource_name', label: 'Details', render: (a) => <span className="text-sm text-gray-600">{a.resource_name || a.http_path || '-'}</span> },
      {
        key: 'status', label: 'Status', sortable: true,
        render: (a) => a.status === 'success' ? (
          <span className="inline-flex items-center space-x-1 text-success-700"><CheckCircle className="w-4 h-4" /><span>Success</span></span>
        ) : (
          <span className="inline-flex items-center space-x-1 text-red-600"><XCircle className="w-4 h-4" /><span>Failed</span></span>
        ),
      },
    ];

    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-500">{activityTotal} events</p>
        <DataTable
          columns={activityColumns}
          data={activities}
          searchPlaceholder="Search activity..."
          loading={activitiesLoading}
        />
      </div>
    );
  };

  const tabs: Tab[] = [
    { id: 'overview', label: 'Overview', icon: Shield, content: <OverviewTab key="overview" /> },
    { id: 'users', label: 'Users', icon: Users, content: <UsersTab key="users" /> },
    { id: 'permissions', label: 'Permissions', icon: CheckSquare, content: <PermissionsTab key="permissions" /> },
    { id: 'activity', label: 'Activity', icon: Activity, content: <ActivityTab key="activity" /> },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/roles')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{role.roleName}</h1>
            <p className="text-gray-600 mt-1">{roleType} • {formatPermissions(rolePermissions)}</p>
          </div>
          {isSystemRole && <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">System</span>}
        </div>
        <div className="flex items-center space-x-3">
          {canUpdate && (
          <button
            onClick={() => {
              if (isSystemRole) {
                toast.info('Not Allowed', 'System roles cannot be edited');
                return;
              }
              setIsEditModalOpen(true);
            }}
            className="btn-primary inline-flex items-center space-x-2"
            disabled={isSystemRole}
          >
            <Edit className="w-4 h-4" />
            <span>Edit Role</span>
          </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="card">
        <Tabs tabs={tabs} />
      </div>

      {/* Edit Role Modal */}
      {roleForModal && !isSystemRole && (
        <RoleModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleSaveRole}
          role={roleForModal}
          mode="edit"
        />
      )}
    </div>
  );
};

export default RoleDetail;
